import React, { useMemo, useState } from 'react'
import {
  DayTimeSelector,
  DAY_TIME_SELECTOR_DEMO_VALUE,
  DAY_TIME_SELECTOR_PREVIEW_STATE_LABELS,
  DAY_TIME_SELECTOR_PREVIEW_STATES,
  EMPTY_DAY_TIME_SELECTOR_VALUE,
  formatDayTimeSelectorValue,
  type DayTimeSelectorPreviewState,
  type DayTimeSelectorValue,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './DayTimeSelectorShowcase.css'

const DayTimeSelectorShowcase: React.FC = () => {
  const [value, setValue] = useState<DayTimeSelectorValue>(
    DAY_TIME_SELECTOR_DEMO_VALUE,
  )
  const [label, setLabel] = useState('Preferred appointment times')
  const [disabled, setDisabled] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<DayTimeSelectorPreviewState>('filled')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'day-time-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="day-time-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as DayTimeSelectorPreviewState,
                  )
                }
              >
                {DAY_TIME_SELECTOR_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {DAY_TIME_SELECTOR_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'day-time-label',
            label: 'Label',
            control: (
              <input
                id="day-time-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'day-time-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="day-time-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'day-time-dark',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="day-time-dark"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, disabled, highlightedState, label],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Day time selector"
        description="Weekday multi-select plus Between/Till time range, as used for Preferred appointment times in Add Patient."
        stageClassName="ui-preview__stage--wide day-time-selector-showcase__stage-wrap"
      >
        <div
          className={`day-time-selector-showcase__sheet${
            darkMode ? ' day-time-selector-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Opens a panel with preferred days (Working days) and a Between /
              Till time range. Summary:{' '}
              {formatDayTimeSelectorValue(value) || 'None'}
            </p>
            <DayTimeSelector
              label={label}
              value={value}
              disabled={disabled}
              darkMode={darkMode}
              onChange={setValue}
            />
          </section>

          <section className="day-time-selector-showcase__matrix">
            <h4 className="day-time-selector-showcase__section-title">
              States
            </h4>
            {DAY_TIME_SELECTOR_PREVIEW_STATES.map((state) => (
              <div
                key={state}
                className={`day-time-selector-showcase__row${
                  state === highlightedState
                    ? ' day-time-selector-showcase__row--selected'
                    : ''
                }${darkMode ? ' day-time-selector-showcase__row--dark' : ''}`}
              >
                <span className="day-time-selector-showcase__row-label">
                  {DAY_TIME_SELECTOR_PREVIEW_STATE_LABELS[state]}
                </span>
                <DayTimeSelector
                  previewState={state}
                  darkMode={darkMode}
                  label={
                    state === 'default'
                      ? 'Preferred appointment times'
                      : undefined
                  }
                />
              </div>
            ))}
          </section>

          <section className="day-time-selector-showcase__matrix">
            <h4 className="day-time-selector-showcase__section-title">
              Empty start
            </h4>
            <div
              className={`day-time-selector-showcase__row${
                darkMode ? ' day-time-selector-showcase__row--dark' : ''
              }`}
            >
              <DayTimeSelector
                label="Preferred appointment times"
                defaultValue={EMPTY_DAY_TIME_SELECTOR_VALUE}
                darkMode={darkMode}
              />
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Day time selector"
        sections={propertySections}
      />
    </>
  )
}

export default DayTimeSelectorShowcase
